import type { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import {
  FINANCIAL_SECTION_URL_VALUE,
  PLANNING_SECTION_URL_SEGMENT,
  TAB_URL,
} from 'partner360/configs'
import {
  ATTENTION_STATUS_BITS,
  getAttentionStatusActions,
  getAttentionStatusRole,
} from 'partner360/entities/attentionStatuses'
import { p360TabsIds } from 'partner360/entities/partnerCompleteness'
import { scrollToElement } from 'partner360/helpers/scrollToElement'
import { resolveJbpPlanActionParam } from 'partner360/lib/jbpPlanActionFlow'

import {
  FinancialCockpitPendingActionOwner,
  type IFinancialCockpitPendingAction,
} from '../../../../api/types'
import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { getCockpitFiscalYearLabel } from '../../../../lib'
import {
  selectCanonicalCockpitFinSobRows,
  selectCockpitPartnerData,
  selectCockpitUserAccess,
} from '../../../../model/selectors'
import { usePendingActionInvocation } from '../../../cockpitPendingActions/hooks/usePendingActionInvocation'
import { useCockpitSectionNavigation } from '../../../components/cockpitSectionNavigation'
import { useAttentionDetailExit } from '../../hooks/useAttentionDetailExit'
import { useAttentionDetailOffering } from '../../hooks/useAttentionDetailOffering'
import { AttentionIntroEnd, AttentionIntroHead } from '../../hooks/useAttentionIntroSlot'
import { usePartnerPbmReminders } from '../../hooks/usePartnerPbmReminders'
import { AttentionStatusActions } from '../attentionStatusActions'

import { buildSobEntryAction, SOB_KEYS } from './buildSobEntryAction'
import { getSobQuarterGrid } from './getSobQuarterGrid'
import { SobPbmList } from './SobPbmList'
import { SobQuarterTable } from './SobQuarterTable'
import { useMissingSobCategories } from './useMissingSobCategories'

import styles from './AttentionStatusSobActuals.module.scss'

const DETAIL_KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.detail

/**
 * The detail of the `SOB Actuals are missing` status: the categories of the offering that still
 * owe their SOB actuals, each with the call that submits them.
 *
 * The card used to list the offering's `jbp-pat-sob` pending actions and nothing else, which left
 * it blank for the partners it matters most for. A pending action is raised inside the collecting
 * window and for the person holding it; the status is raised whenever a figure is missing. So a
 * partner who has submitted no SOB at all raises the status with no action to show for it, and the
 * card said `nothing is waiting` over a status the server had just raised.
 *
 * What is missing is not a field of the payload — it is the difference between two of them.
 * `ActualsAndTargetsSettings` says which categories the offering collects SOB on and which of
 * those this partner owes; `PartnerFinSobData` says which of them a figure has come in for. The
 * categories below are the first minus the second, computed in `getMissingSobCategories` so the
 * rule can be read and tested apart from the card. A `jbp-pat-sob` action, where the payload
 * carries one, is still the way that category is updated — it is the server naming the category
 * outright, and it carries its own owner, its blocked-by lock and its dialog's parameters.
 *
 * Every row opens the SOB dialog on its category, the way the H2 review opens its items, whoever
 * reads it; a blocked row wears its lock instead. The one reminder the sheet asks for stays in the
 * bar, not once per category.
 *
 * With nothing to list the card says which of the two silences it is looking at — an offering the
 * payload names no SOB category for, or one whose every category is answered while the server
 * still raises the status — rather than the flat `nothing is waiting` that hid the bug.
 */
export const AttentionStatusSobActuals = () => {
  const activeOffering = useAttentionDetailOffering()

  /* The status is raised per offering, and the cockpit's filters decide which offerings a partner
     shows: with none of them left there is nothing to submit actuals for. */
  if (!activeOffering) {
    return (
      <div className={styles.root}>
        <p className={styles.empty}>
          <Translation span text={DETAIL_KEYS.noOffering} />
        </p>
      </div>
    )
  }

  return <SobActualsBody offering={activeOffering} />
}

const SobActualsBody = ({ offering }: { offering: ProgramTypes }) => {
  const { openSection } = useCockpitSectionNavigation()
  const { role } = useSelector(selectCockpitUserAccess)
  const finSobRows = useSelector(selectCanonicalCockpitFinSobRows)
  const partnerId = useSelector(selectCockpitPartnerData)[0]?.PartnerId ?? ''
  const { categories, state, fYear, fQuarter } = useMissingSobCategories(offering)
  const { selectAction, isActionBusy, renderBlockedTooltip, modals } =
    usePendingActionInvocation(offering)
  const { recipients, reminder } = usePartnerPbmReminders(
    partnerId,
    offering,
    ATTENTION_STATUS_BITS.sobActuals
  )
  const exitDetail = useAttentionDetailExit()

  /* The sheet's role column, read through the same helper the action bar uses, so the card cannot
     offer an update or a reminder the bar withholds. */
  const roleActionIds = getAttentionStatusActions(
    ATTENTION_STATUS_BITS.sobActuals,
    getAttentionStatusRole(role)
  ).map(({ id }) => id)
  const canUpdate = roleActionIds.includes('updateSob')
  const canRemind = roleActionIds.includes('remindPbm')

  /* `FY26 Q3` where the payload names the quarter SOB is collected for, `FY26` where it does not
     and the whole year is read instead. */
  const periodLabel = [getCockpitFiscalYearLabel(fYear), fQuarter === null ? '' : `Q${fQuarter}`]
    .filter(Boolean)
    .join(' ')

  const rows = useMemo(
    () =>
      categories.map(({ category, action }, index) => ({
        category,
        action:
          action ??
          buildSobEntryAction(
            category,
            canUpdate
              ? FinancialCockpitPendingActionOwner.Mine
              : FinancialCockpitPendingActionOwner.PBM,
            index
          ),
        grid: getSobQuarterGrid({ rows: finSobRows, offering, category, fYear, fQuarter }),
      })),
    [canUpdate, categories, fQuarter, fYear, finSobRows, offering]
  )

  const handleOpen = useCallback(
    (action: IFinancialCockpitPendingAction) => {
      if (selectAction(action)) {
        exitDetail()
      }
    },
    [exitDetail, selectAction]
  )

  /* The cockpit sits on the partner's own page, so the link opens the FinSOB section right there:
     the planning tab, scrolled to the first category still owing actuals. With none left the
     helper falls back to the section's own total. */
  const handleGoToSection = useCallback(() => {
    scrollToElement(p360TabsIds)

    openSection({
      [TAB_URL]: FINANCIAL_SECTION_URL_VALUE,
      [PLANNING_SECTION_URL_SEGMENT]: resolveJbpPlanActionParam(categories[0]?.category),
    })
    exitDetail()
  }, [categories, exitDetail, openSection])

  return (
    <div className={styles.root}>
      {rows.length === 0 ? (
        <p className={styles.empty}>
          {/* Which silence it is, said plainly. Both readings leave the reader with nothing to do
          here, and only one of them means the payload is short of what the status needs. */}
          <Translation
            span
            text={state === 'noCatalog' ? SOB_KEYS.noCategories : SOB_KEYS.allSubmitted}
            replaceParams={[{ key: '{period}', value: periodLabel }]}
          />
        </p>
      ) : (
        <SobQuarterTable
          rows={rows}
          fYear={fYear}
          canUpdate={canUpdate}
          onOpen={handleOpen}
          isActionBusy={isActionBusy}
          renderBlockedTooltip={renderBlockedTooltip}
        />
      )}

      {modals}

      {/* Who the figures wait for opens the text column, the reminder beside each of them. */}
      <AttentionIntroHead>
        <SobPbmList pbms={recipients} reminder={reminder} canRemind={canRemind} />
      </AttentionIntroHead>

      <AttentionIntroEnd>
        <button type="button" className={styles.sectionLink} onClick={handleGoToSection}>
          <i className="kbase kbase-external-link" aria-hidden="true" />
          <Translation span text={SOB_KEYS.openSection} />
        </button>
      </AttentionIntroEnd>

      {/* The update is the rows, the reminder the PBM list and the way to the section closes the
      lead sentence, so the bar keeps nothing of its own here. */}
      <AttentionStatusActions
        bitValue={ATTENTION_STATUS_BITS.sobActuals}
        className={styles.actions}
        hidden={['updateSob', 'goToSection', 'remindPbm']}
      />
    </div>
  )
}

AttentionStatusSobActuals.displayName = 'AttentionStatusSobActuals'
